import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Download, FileUp, Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { apiErrorHandler } from "@/core/api/api-error-handler";
import { notifSilenceError } from "@/core/utils/func/notifApp";
import { usePayrollBatches } from "@/features/payroll/api/use-payroll-batches";
import { useDownloadPayrollTemplate } from "@/features/payroll/api/use-download-payroll-template";
import type {
  PayrollBatchFilter,
  PayrollBatchListItemDto,
} from "@/features/payroll/api/dto";
import type { PayrollBatchStatus } from "@/features/payroll/api/enums";
import {
  payrollBatchesColumns,
  PayrollBatchCreateDialog,
  PayrollBatchImportDialog,
  PayrollStatsCards,
  type PayrollBatchRow,
} from "@/features/payroll/components";

/** Traduit les filtres de colonnes DataTableClar en `PayrollBatchFilter`. */
const toPayrollBatchFilter = (
  filters: ColumnFiltersState
): PayrollBatchFilter => {
  const filter: PayrollBatchFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "status") filter.status = value as PayrollBatchStatus;
  }
  return filter;
};

/**
 * Liste paginée des paies de masse (`POST /PayrollBatches/all`, guide v4.1
 * §6.11) — cartes de stats en tête, filtre `status`, actions boutons
 * « Télécharger le modèle », « Nouvelle paie », « Importer un Excel ». Route
 * cible : `/app/payroll`.
 */
export function PayrollBatchesPage() {
  const { t } = useTypedTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toPayrollBatchFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } =
    usePayrollBatches(params);

  const downloadTemplate = useDownloadPayrollTemplate();

  const rows = useMemo(
    () =>
      (data?.items ?? []).filter(
        (batch: PayrollBatchListItemDto): batch is PayrollBatchRow =>
          Boolean(batch.id)
      ),
    [data]
  );

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("payroll.batches.title")}
        </h1>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            disabled={downloadTemplate.isPending}
            onClick={() =>
              downloadTemplate.mutate(undefined, {
                onError: (err) =>
                  notifSilenceError(apiErrorHandler(err) || err.message),
              })
            }
          >
            {downloadTemplate.isPending ? (
              <ButtonSpinner />
            ) : (
              <Download className="mr-2 h-4 w-4" />
            )}
            {t("payroll.batches.template.cta")}
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)}>
            <FileUp className="mr-2 h-4 w-4" />
            {t("payroll.batches.import.cta")}
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t("payroll.batches.create.cta")}
          </Button>
        </div>
      </div>

      <PayrollStatsCards />

      <DataTableClar
        data={rows}
        columnsClar={payrollBatchesColumns}
        title={t("payroll.batches.title")}
        exportFileName="payroll-batches"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <PayrollBatchCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <PayrollBatchImportDialog open={importOpen} onOpenChange={setImportOpen} />
    </section>
  );
}
